import { v4 as uuidv4 } from 'uuid';
import { defaultUnitsV2 } from '../../../../sustell_15/utils/unit-utils';
import { UserProfilePrefs } from '../../../Helpers/UserProfilePrefs';
import {
    ShrimpProcessing,
    ShrimpProcessingInput,
    ShrimpProcessingInputType,
    ShrimpProcessingOutput,
    ShrimpProcessingOutputItem,
    ShrimpProcessingPackaging,
    ShrimpProcessingPackagingItem,
    ShrimpProcessingStageData,
    TransportTypeDistanceIce
} from "../../../../sustell_15/models/Baseline/ShrimpBaseline";
import {
    ProcessingPackagingItemDBModel,
    StageType
} from "../../../../sustell_15/models/TempBackendModels/TempProcessingDB";
import {
    ProcessingInputItemDBModel,
    ShrimpPackagingDBModel,
    ShrimpProcessingDBModel,
    ShrimpProcessingInputDBModel,
    ShrimpProcessingOutputDBModel,
    ShrimpProcessingOutputItemDBModel,
    ShrimpProcessingPackagingDBModel,
    ShrimpProcessingStageDataDBModel,
    TransportWithIceDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempShrimpDB";
import { ProcessingPackagingItem } from '../../../../sustell_15/models/Facility/Processing';
import {
    PrimaryPackagingType,
    SecondaryPackagingType,
    TertiaryPackagingType
} from '../../../../../graphql/generated/blonk/shrimp';
import { formatInMassInput, formatInTransportInput } from './inMapperHelpers';
import { TransportMode } from '../../../../../graphql/types';
import { floatValueInUserUnits } from './inCommonMapperSustellV2';

const mapProcessingInputForOneStage = (
    inputBlockData: ShrimpProcessingInputDBModel
): ShrimpProcessingInput => {
    if (!inputBlockData) return {} as ShrimpProcessingInput;

    const userPrefs = UserProfilePrefs.getInstance();
    const userUOM = userPrefs.getUserUnitPrefs();
    const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

    const mappedProcessingInput: ShrimpProcessingInput = {
      averageWeight: floatValueInUserUnits(
        inputBlockData.averageWeight,
        userUOM.unitBarnSmallQuantityUnits,
        defaultUnitsV2.unitBarnSmallQuantityUnits,
        outputMassPrecision,
      ),
      sources: [],
    };


    inputBlockData.sources.forEach((shrimpSource: ProcessingInputItemDBModel) => {
        const source: ShrimpProcessingInputType = {
            id: shrimpSource.id || uuidv4(),
            originStageId: shrimpSource.originStageId,
            originOutputId: shrimpSource.originOutputId,
            totalWeight: formatInMassInput(shrimpSource.totalWeight),
            transport: [],
        }

        shrimpSource.transport.forEach((sourceTransport: TransportWithIceDBModel) => {
            const transport: TransportTypeDistanceIce = {
                id: sourceTransport.id || uuidv4(),
                transportMode: sourceTransport.mode as TransportMode,
                distance: formatInTransportInput(sourceTransport.distance, sourceTransport.mode as TransportMode),
                iceAmount: floatValueInUserUnits(
                    sourceTransport.iceAmount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            }

            source.transport.push(transport);
        });

        mappedProcessingInput.sources.push(source);
    });

    return mappedProcessingInput;
}

const mapProcessingOutputForOneStage = (
    outputBlockData: ShrimpProcessingOutputDBModel
): ShrimpProcessingOutput => {
    if (!outputBlockData) return {} as ShrimpProcessingOutput;

    const mappedProcessingOutput: ShrimpProcessingOutput = {
        freshMeatAndEdibleOffal: [],
        cat3SlaughterByProducts: [],
        cat1And2MaterialAndWaste: [],
    };

    const mapCategoryOutput = (
        categoryOutput: ShrimpProcessingOutputItemDBModel
    ): ShrimpProcessingOutputItem => {
        if (!categoryOutput) return {} as ShrimpProcessingOutputItem;

        const userPrefs = UserProfilePrefs.getInstance();
        const userUOM = userPrefs.getUserUnitPrefs();
        const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

        const mappedOutput: ShrimpProcessingOutputItem = {
            outputId: categoryOutput.outputId || uuidv4(),
            outputName: categoryOutput.outputName,
            coolingType: categoryOutput.coolingType,
            calculate: categoryOutput.calculate,
            weight: floatValueInUserUnits(
                categoryOutput.weight,
                userUOM.unitBarnOutputMass,
                defaultUnitsV2.unitBarnOutputMass,
                outputMassPrecision
            ),
            price: categoryOutput.price,
        }

        return mappedOutput;
    }

    outputBlockData.freshMeatAndEdibleOffal?.forEach((edibleOutput: ShrimpProcessingOutputItemDBModel) => {
        const output: ShrimpProcessingOutputItem = mapCategoryOutput(edibleOutput);
        mappedProcessingOutput.freshMeatAndEdibleOffal?.push(output);
    });

    outputBlockData.cat3SlaughterByProducts?.forEach((cat3Output: ShrimpProcessingOutputItemDBModel) => {
        const output: ShrimpProcessingOutputItem = mapCategoryOutput(cat3Output);
        mappedProcessingOutput.cat3SlaughterByProducts?.push(output);
    });

    outputBlockData.cat1And2MaterialAndWaste?.forEach((cat1and2Output: ShrimpProcessingOutputItemDBModel) => {
        const output: ShrimpProcessingOutputItem = mapCategoryOutput(cat1and2Output);
        mappedProcessingOutput.cat1And2MaterialAndWaste?.push(output);
    });

    return mappedProcessingOutput;
}

const mapProcessingPackagingForOneStage = (
    packagingBlockData: ShrimpProcessingPackagingDBModel
): ShrimpProcessingPackaging => {
    if (!packagingBlockData) return {} as ShrimpProcessingPackaging;

    const userPrefs = UserProfilePrefs.getInstance();
    const userUOM = userPrefs.getUserUnitPrefs();
    const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();

    const mappedProcessingPackaging: ShrimpProcessingPackaging = {
        freshMeatAndEdibleOffal: [],
        cat3SlaughterByProducts: [],
        cat1And2MaterialAndWaste: [],
    };

    const mapPackagingCategory = (
        categoryItem: ShrimpPackagingDBModel
    ): ShrimpProcessingPackagingItem => {
        if (!categoryItem) return {} as ShrimpProcessingPackagingItem;

        const categoryPackaging: ShrimpProcessingPackagingItem = {
            outputId: categoryItem.outputId,
            primary: [],
            secondary: [],
            tertiary: [],
        };

        categoryItem.primary?.forEach((primary: ProcessingPackagingItemDBModel<PrimaryPackagingType>) => {
            const packaging: ProcessingPackagingItem<PrimaryPackagingType> = {
                id: primary.id || uuidv4(),
                type: primary.type,
                amount: floatValueInUserUnits(
                    primary.amount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            };

            categoryPackaging.primary?.push(packaging);
        });

        categoryItem.secondary?.forEach((secondary: ProcessingPackagingItemDBModel<SecondaryPackagingType>) => {
            const packaging: ProcessingPackagingItem<SecondaryPackagingType> = {
                id: secondary.id || uuidv4(),
                type: secondary.type,
                amount: floatValueInUserUnits(
                    secondary.amount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            };

            categoryPackaging.secondary?.push(packaging);
        });

        categoryItem.tertiary?.forEach((tertiary: ProcessingPackagingItemDBModel<TertiaryPackagingType>) => {
            const packaging: ProcessingPackagingItem<TertiaryPackagingType> = {
                id: tertiary.id || uuidv4(),
                type: tertiary.type,
                amount: floatValueInUserUnits(
                    tertiary.amount,
                    userUOM.unitOutputMass,
                    defaultUnitsV2.unitOutputMass,
                    outputMassPrecision
                ),
            };

            categoryPackaging.tertiary?.push(packaging);
        });

        return categoryPackaging;
    }

    packagingBlockData.freshMeatAndEdibleOffal?.forEach((ediblePackaging: ShrimpPackagingDBModel) => {
        const category: ShrimpProcessingPackagingItem = mapPackagingCategory(ediblePackaging);
        mappedProcessingPackaging.freshMeatAndEdibleOffal?.push(category);
    });

    packagingBlockData.cat3SlaughterByProducts?.forEach((cat3Packaging: ShrimpPackagingDBModel) => {
        const category: ShrimpProcessingPackagingItem = mapPackagingCategory(cat3Packaging);
        mappedProcessingPackaging.cat3SlaughterByProducts?.push(category);
    });

    packagingBlockData.cat1And2MaterialAndWaste?.forEach((cat1and2Packaging: ShrimpPackagingDBModel) => {
        const category: ShrimpProcessingPackagingItem = mapPackagingCategory(cat1and2Packaging);
        mappedProcessingPackaging.cat1And2MaterialAndWaste?.push(category);
    });

    return mappedProcessingPackaging;
}

export const mapInShrimpProcessingStage = (
    incomingData: ShrimpProcessingDBModel
): ShrimpProcessing => {
    if (!incomingData) return {} as ShrimpProcessing;

    const stageData = typeof incomingData.stageData === 'string'
        ? (JSON.parse(String(incomingData.stageData)) as ShrimpProcessingStageDataDBModel)
        : incomingData.stageData;

    try {
        const mappedStage: Omit<ShrimpProcessing, 'productionSystem' | 'facilityType'> = {
            facilityId: incomingData.facilityId,
            type: incomingData?.type || StageType.Processing,
            farmId: incomingData?.farmId || '',
            name: incomingData?.name || '',
            id: incomingData?.id,
            stageData: {
                processingInput: mapProcessingInputForOneStage(
                    stageData.processingInput as ShrimpProcessingInputDBModel
                ),
                processingOutput: mapProcessingOutputForOneStage(
                    stageData.processingOutput as ShrimpProcessingOutputDBModel
                ),
                processingPackaging: mapProcessingPackagingForOneStage(
                    stageData.processingPackaging as ShrimpProcessingPackagingDBModel
                ),
            } as ShrimpProcessingStageData,
        };

        return mappedStage as ShrimpProcessing;
    } catch (err) {
        console.error("Error on mapOutProcessingStage: ", err);
    }

    return {} as ShrimpProcessing;
};